import { NetworkStatus } from "@apollo/client";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: loading-state UIVStack carries data-driven responsive content width (widthStyle(widthContent) = 750px off-scale) via style;…
import { ListRenderItem } from "@shopify/flash-list";
import { Stack, useLocalSearchParams } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import MatchesView from "./MatchesView";
import ListEmpty from "@/features/reports/components/molecules/ListEmpty";
import MatchItem from "@/features/reports/components/molecules/MatchItem";

import { Stack as VStack } from "@/components/atoms";
import SkeletonList from "@/components/organisms/SkeletonList";
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: loading-state UIVStack carries data-driven responsive content width (widthStyle(widthContent) = 750px off-scale) via style;…
import { useBreakpointValue } from "@/components/ui/utils/use-break-point-value";
import { spacingForDevicesWithoutNotch } from "@/config/constants";
import {
  ReportsPlayerMatchFragment,
  useReportsListPlayerMatchesQuery,
} from "@/generated/graphql";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";

// design-system: dynamic-style waiver — breakpoint-computed responsive content
// width; no closed sizing token expresses it. Passed as a single style ref.
const widthStyle = (width: number) => ({ width });

/**
 * Lists player matches for scout report creation with multi-select capability.
 * Enables selection of multiple matches before navigating to the intake form.
 * @module features/reports/screens/Matches
 */
const MatchesContainer = () => {
  const getExpandedUrl = useGetExpandedPlayerUrl();
  const [selectedMatchIds, setSelectedMatchIds] = useState<string[]>([]);
  const { bottom } = useSafeAreaInsets();
  const { playerId, hideBackButton } = useLocalSearchParams<{
    playerId: string;
    hideBackButton?: string;
  }>();
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const intakeUrl = useMemo(
    () =>
      getExpandedUrl(
        `/players/${playerId}/matches/${selectedMatchIds.join(",") || "none"}/intake`
      ),
    [getExpandedUrl, playerId, selectedMatchIds]
  );

  const { data, loading, fetchMore, networkStatus, refetch } =
    useReportsListPlayerMatchesQuery({
      variables: { playerId },
    });

  const refreshing = networkStatus === NetworkStatus.refetch;

  const endCursor = data?.listPlayerMatches.pageInfo.endCursor;
  const hasNextPage = data?.listPlayerMatches.pageInfo.hasNextPage;
  const items = useMemo(
    () =>
      data?.listPlayerMatches.edges.filter(Boolean).map(edge => edge?.node) ??
      [],
    [data]
  );

  const handleSelectMatch = useCallback((matchId: string) => {
    setSelectedMatchIds(prev =>
      prev.includes(matchId)
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId]
    );
  }, []);

  const handleLoadMore = useCallback(() => {
    if (endCursor && hasNextPage && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, hasNextPage, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const createMatchSelectHandler = useCallback(
    (matchId: string) => () => handleSelectMatch(matchId),
    [handleSelectMatch]
  );

  const renderItem: ListRenderItem<ReportsPlayerMatchFragment> = useCallback(
    ({ item }) => (
      <MatchItem
        selectedMatchIds={selectedMatchIds}
        onSelect={createMatchSelectHandler(item.matchId)}
        item={item}
      />
    ),
    [createMatchSelectHandler, selectedMatchIds]
  );

  const renderListEmptyComponent = useCallback(
    () => (
      <ListEmpty
        title="No Matches"
        description="We do not have any matches for this player."
      />
    ),
    []
  );
  const { width } = useWindowDimensions();
  const widthContent = useBreakpointValue({ default: width, md: 750 });
  return (
    <>
      {/* Reached from the player preview modal (dismissed on navigation) — drop
          the back arrow so this screen doesn't dead-end (SE-5185 follow-up). */}
      {hideBackButton === "true" ? (
        <Stack.Screen options={{ headerBackVisible: false }} />
      ) : null}
      {loading ? (
        <VStack
          className="self-center"
          // eslint-disable-next-line no-restricted-syntax -- data-driven responsive content width (window width or 750px off-scale); no closed sizing token expresses it
          UNSAFE_style={widthStyle(widthContent)}
        >
          <SkeletonList type="note" />
        </VStack>
      ) : (
        <MatchesView
          intakeUrl={intakeUrl}
          selectedMatchIds={selectedMatchIds}
          items={items}
          refreshing={refreshing}
          isDark={isDark}
          widthContent={widthContent}
          bottomInset={bottom + spacingForDevicesWithoutNotch}
          renderItem={renderItem}
          onLoadMore={handleLoadMore}
          onRefresh={handleRefresh}
          renderListEmptyComponent={renderListEmptyComponent}
        />
      )}
    </>
  );
};

export default MatchesContainer;
